import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Robot, BookBookmark, Trophy, Lightning, Target, ChatCircleDots,
  ArrowRight, CrownSimple, Sparkle, FireSimple, Timer, ShareNetwork, Cards,
} from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, ResponsiveContainer, Cell } from "recharts";
import { toast } from "sonner";
import { MASCOT } from "@/lib/assets";

function StatCard({ icon: Icon, label, value, tone, tid }) {
  return (
    <div data-testid={tid} className="rounded-2xl border border-border bg-white p-5">
      <div className="grid h-10 w-10 place-items-center rounded-xl" style={{ background: `${tone}1a`, color: tone }}>
        <Icon size={22} weight="duotone" />
      </div>
      <div className="mt-3 font-display text-2xl font-extrabold">{value}</div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [shareCode, setShareCode] = useState("");
  const [learning, setLearning] = useState(null);

  useEffect(() => {
    if (user?.role === "parent") { navigate("/app/parent", { replace: true }); return; }
    if (user?.role === "teacher") { navigate("/app/enseignant", { replace: true }); return; }
    if (user?.role === "school") { navigate("/app/etablissement", { replace: true }); return; }
    if (user?.role === "admin") { navigate("/app/admin", { replace: true }); return; }
    api.get("/dashboard/student").then((r) => setData(r.data)).catch(() => {});
    api.get("/student/share-code").then((r) => setShareCode(r.data.code)).catch(() => {});
    api.get("/learning/summary").then((r) => setLearning(r.data)).catch(() => {});
  }, [user, navigate]);

  const lvl = data?.level;
  const nxt = data?.next_level;
  const progress = nxt ? Math.min(100, Math.round(((data.xp - lvl.min_xp) / (nxt.min_xp - lvl.min_xp)) * 100)) : 100;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 animate-fade-up">
        <div>
          <p className="text-sm text-muted-foreground">{new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold sm:text-4xl">Bonjour, {user?.name?.split(" ")[0]} 👋</h1>
          <p className="mt-1 text-ink/65">{data?.exam ? `Vous préparez le ${data.exam.name} (${data.exam.classe}). Continuez sur votre lancée !` : "Continuez votre préparation."}</p>
        </div>
        <img src={MASCOT.wave} alt="" className="hidden h-28 w-28 shrink-0 object-contain sm:block" />
      </div>

      {/* Subscription banner */}
      {data && !data.subscription && (
        <div className="flex flex-col items-start justify-between gap-3 rounded-2xl border border-terracotta/30 bg-terracotta/5 p-5 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-terracotta/15 text-terracotta"><CrownSimple weight="fill" size={22} /></div>
            <div>
              <div className="font-semibold">Débloquez tout le potentiel</div>
              <div className="text-sm text-muted-foreground">Accédez à l'IA illimitée et au mode hors-ligne dès 500 FCFA.</div>
            </div>
          </div>
          <Link to="/app/tarifs" data-testid="dash-upgrade" className="rounded-full bg-terracotta px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#E03E00]">Voir les offres</Link>
        </div>
      )}

      {/* Share code for parent/teacher linking */}
      {shareCode && (
        <div className="flex flex-col items-start justify-between gap-3 rounded-2xl border border-forest/20 bg-forest/5 p-5 sm:flex-row sm:items-center" data-testid="share-code-card">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-forest/15 text-forest"><ShareNetwork weight="fill" size={22} /></div>
            <div>
              <div className="font-semibold">Mon code de partage</div>
              <div className="text-sm text-muted-foreground">Partagez-le avec vos parents ou enseignants pour qu'ils suivent votre progression.</div>
            </div>
          </div>
          <button
            data-testid="share-code-value"
            onClick={() => { navigator.clipboard?.writeText(shareCode); toast.success("Code copié !"); }}
            className="rounded-xl bg-forest px-5 py-2.5 font-display text-lg font-extrabold tracking-widest text-white">
            {shareCode}
          </button>
        </div>
      )}

      {/* Mastery + révisions du jour */}
      {learning && (
        <div className="grid gap-4 lg:grid-cols-3" data-testid="learning-card">
          <Link to="/app/revisions" data-testid="review-due-card"
            className="flex items-center justify-between rounded-2xl border border-terracotta/30 bg-terracotta/5 p-5 transition hover:-translate-y-0.5 hover:shadow-md">
            <div>
              <div className="flex items-center gap-2 font-semibold text-terracotta"><Cards weight="fill" /> Révisions du jour</div>
              <div className="mt-1 text-sm text-muted-foreground">{learning.errors_total} erreur(s) au carnet · {learning.errors_mastered} maîtrisée(s)</div>
            </div>
            <div className="text-right">
              <div className="font-display text-3xl font-extrabold text-terracotta">{learning.review_due}</div>
              <div className="text-xs text-muted-foreground">à revoir</div>
            </div>
          </Link>
          <div className="rounded-2xl border border-border bg-white p-5 lg:col-span-2">
            <div className="flex items-center gap-2 font-semibold"><Target weight="fill" className="text-forest" /> Ma maîtrise par matière</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {learning.mastery.length === 0 && <span className="text-sm text-muted-foreground">Commencez un quiz pour suivre votre maîtrise.</span>}
              {learning.mastery.map((m) => {
                const style = {
                  confirme: "bg-forest/10 text-forest", a_consolider: "bg-gold/15 text-[#8a6d1e]",
                  en_cours: "bg-blue-100 text-blue-700", a_decouvrir: "bg-sand text-muted-foreground",
                }[m.state];
                const label = { confirme: "Confirmé", a_consolider: "À consolider", en_cours: "En cours", a_decouvrir: "À découvrir" }[m.state];
                return (
                  <span key={m.subject} data-testid={`mastery-${m.subject}`} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${style}`}>
                    {m.subject} · {label}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Level card */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-border bg-forest p-6 text-white lg:col-span-1">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60">Niveau {lvl?.level}</span>
            <FireSimple weight="fill" className="text-gold" size={22} />
          </div>
          <div className="mt-2 font-display text-2xl font-extrabold">{lvl?.name || "—"}</div>
          <div className="mt-5">
            <div className="flex justify-between text-sm text-white/70"><span>{data?.xp ?? 0} XP</span>{nxt && <span>{nxt.min_xp} XP</span>}</div>
            <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-white/15">
              <div className="h-full rounded-full bg-gold transition-all" style={{ width: `${progress}%` }} />
            </div>
            <p className="mt-2 text-xs text-white/60">{nxt ? `Encore ${nxt.min_xp - data.xp} XP pour devenir ${nxt.name}` : "Niveau maximum atteint !"}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 lg:col-span-2">
          <StatCard tid="stat-quizzes" icon={Target} label="Quiz complétés" value={data?.quizzes_done ?? 0} tone="#FF4B0A" />
          <StatCard tid="stat-avg" icon={Lightning} label="Score moyen" value={`${data?.avg_score ?? 0}%`} tone="#042A65" />
          <StatCard tid="stat-badges" icon={Trophy} label="Badges gagnés" value={data?.badges_count ?? 0} tone="#FFB800" />
          <StatCard tid="stat-chats" icon={ChatCircleDots} label="Sessions IA" value={data?.chats ?? 0} tone="#355070" />
        </div>
      </div>

      {/* Success probability */}
      <div className="rounded-2xl border border-border bg-white p-6" data-testid="success-probability">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-display text-lg font-bold">Probabilité de réussite estimée</h2>
            <p className="text-sm text-muted-foreground">Basée sur vos quiz, votre régularité et vos matières couvertes.</p>
          </div>
          <div className="text-right">
            <div className="font-display text-4xl font-extrabold" style={{ color: (data?.success_probability ?? 0) >= 60 ? "#042A65" : (data?.success_probability ?? 0) >= 35 ? "#FFB800" : "#FF4B0A" }}>
              {data?.success_probability ?? 0}%
            </div>
          </div>
        </div>
        <div className="mt-4 h-3 overflow-hidden rounded-full bg-sand">
          <div className="h-full rounded-full transition-all" style={{ width: `${data?.success_probability ?? 0}%`, background: (data?.success_probability ?? 0) >= 60 ? "#042A65" : (data?.success_probability ?? 0) >= 35 ? "#FFB800" : "#FF4B0A" }} />
        </div>
        {!data?.quizzes_done && <p className="mt-3 text-sm text-muted-foreground">Faites vos premiers quiz ou un simulateur d'examen pour obtenir votre estimation.</p>}
      </div>

      {/* Quick actions */}
      <div>
        <h2 className="mb-4 font-display text-xl font-bold">Continuer</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { to: "/app/tuteur", icon: Robot, title: "Professeur IA", desc: "Posez vos questions", color: "#FF4B0A", tid: "quick-tutor" },
            { to: "/app/cours", icon: BookBookmark, title: "Mes cours", desc: "Réviser une matière", color: "#042A65", tid: "quick-courses" },
            { to: "/app/simulateur", icon: Timer, title: "Examen blanc", desc: "En temps réel", color: "#FFB800", tid: "quick-simulator" },
          ].map((a) => {
            const Icon = a.icon;
            return (
              <Link key={a.to} to={a.to} data-testid={a.tid} className="group flex items-center gap-4 rounded-2xl border border-border bg-white p-5 transition hover:-translate-y-1 hover:shadow-[0_14px_34px_rgba(4,42,101,0.1)]">
                <div className="grid h-12 w-12 place-items-center rounded-xl" style={{ background: `${a.color}1a`, color: a.color }}><Icon size={24} weight="duotone" /></div>
                <div className="flex-1">
                  <div className="font-semibold">{a.title}</div>
                  <div className="text-sm text-muted-foreground">{a.desc}</div>
                </div>
                <ArrowRight className="text-muted-foreground transition group-hover:translate-x-1 group-hover:text-terracotta" />
              </Link>
            );
          })}
        </div>
      </div>

      {/* Performance + recent */}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-white p-6">
          <h2 className="font-display text-lg font-bold">Performance par matière</h2>
          {data?.subject_stats?.length ? (
            <div className="mt-4 h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.subject_stats}>
                  <XAxis dataKey="subject" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                  <Bar dataKey="avg" radius={[8, 8, 0, 0]}>
                    {data.subject_stats.map((_, i) => <Cell key={i} fill={i % 2 ? "#042A65" : "#FF4B0A"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="mt-6 flex flex-col items-center justify-center rounded-xl bg-sand py-10 text-center">
              <Sparkle weight="fill" className="text-terracotta" size={28} />
              <p className="mt-2 text-sm text-muted-foreground">Faites votre premier quiz pour voir vos statistiques.</p>
              <Link to="/app/cours" className="mt-3 rounded-full bg-terracotta px-4 py-2 text-sm font-semibold text-white">Commencer</Link>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-white p-6">
          <h2 className="font-display text-lg font-bold">Activité récente</h2>
          {data?.recent_attempts?.length ? (
            <div className="mt-4 space-y-3">
              {data.recent_attempts.map((a, i) => (
                <div key={i} className="flex items-center justify-between rounded-xl bg-cream px-4 py-3">
                  <div>
                    <div className="text-sm font-semibold">{a.subject}</div>
                    <div className="text-xs text-muted-foreground">{new Date(a.created_at).toLocaleDateString("fr-FR")}</div>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-sm font-bold ${a.score >= 50 ? "bg-forest/10 text-forest" : "bg-destructive/10 text-destructive"}`}>{a.score}%</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-6 text-center text-sm text-muted-foreground">Aucune activité pour l'instant.</p>
          )}
        </div>
      </div>
    </div>
  );
}
